import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type {
  Asset,
  AssetFolder,
  AssetBreadcrumb,
  AssetsIndexData,
  AssetOwnerType
} from '@/types/assets'

interface UseAssetsOptions {
  ownerType: AssetOwnerType
  ownerId: number
  initialData?: AssetsIndexData
  isActive?: boolean
}

interface UseAssetsReturn {
  folders: AssetFolder[]
  assets: Asset[]
  currentFolder: AssetFolder | null
  breadcrumbs: AssetBreadcrumb[]
  isLoading: boolean
  error: string | null

  // Navigation
  navigateToFolder: (folderId: number | null) => Promise<void>
  refresh: () => Promise<void>

  // Asset operations
  uploadAsset: (signedBlobId: string, name?: string) => Promise<Asset | null>
  renameAsset: (assetId: number, name: string) => Promise<boolean>
  moveAsset: (assetId: number, folderId: number | null) => Promise<boolean>
  copyAssetToProject: (assetId: number, projectId: number, folderId?: number | null) => Promise<Asset | null>
  copyAssetToOrganization: (assetId: number, folderId?: number | null) => Promise<Asset | null>
  deleteAsset: (assetId: number) => Promise<boolean>

  // Folder operations
  createFolder: (name: string, parentId?: number | null) => Promise<AssetFolder | null>
  renameFolder: (folderId: number, name: string) => Promise<boolean>
  deleteFolder: (folderId: number) => Promise<boolean>

  // Folder tree
  folderTree: AssetFolder[]
  loadFolderTree: () => Promise<void>
}

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function getBasePath(ownerType: AssetOwnerType, ownerId: number): string {
  if (ownerType === 'organization') {
    return '/organization'
  }
  return `/projects/${ownerId}`
}

export function useAssets({ ownerType, ownerId, initialData, isActive }: UseAssetsOptions): UseAssetsReturn {
  const { t } = useTranslation('assets')
  const [folders, setFolders] = useState<AssetFolder[]>(initialData?.folders || [])
  const [assets, setAssets] = useState<Asset[]>(initialData?.assets || [])
  const [currentFolder, setCurrentFolder] = useState<AssetFolder | null>(initialData?.current_folder || null)
  const [breadcrumbs, setBreadcrumbs] = useState<AssetBreadcrumb[]>(initialData?.breadcrumbs || [])
  const [folderTree, setFolderTree] = useState<AssetFolder[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const basePath = getBasePath(ownerType, ownerId)

  const fetchAssets = useCallback(async (folderId: number | null) => {
    setIsLoading(true)
    setError(null)

    try {
      const url = folderId
        ? `${basePath}/assets?folder_id=${folderId}`
        : `${basePath}/assets`

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch assets')
      }

      const data: AssetsIndexData = await response.json()
      setFolders(data.folders)
      setAssets(data.assets)
      setCurrentFolder(data.current_folder)
      setBreadcrumbs(data.breadcrumbs)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.fetchFailed')
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }, [basePath, t])

  // Auto-fetch on mount if no initialData provided
  useEffect(() => {
    if (!initialData) {
      fetchAssets(null)
    }
  }, [])

  // Refetch when tab becomes active (e.g. after server-side asset creation)
  const hasMounted = useRef(false)
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    if (isActive) {
      fetchAssets(currentFolder?.id || null)
    }
  }, [isActive])

  const navigateToFolder = useCallback(async (folderId: number | null) => {
    await fetchAssets(folderId)
  }, [fetchAssets])

  const refresh = useCallback(async () => {
    await fetchAssets(currentFolder?.id || null)
  }, [fetchAssets, currentFolder])

  const loadFolderTree = useCallback(async () => {
    try {
      const response = await fetch(`${basePath}/asset_folders`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch folder tree')
      }

      const data = await response.json()
      setFolderTree(data.folders)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.fetchFolderTreeFailed')
      toast.error(message)
    }
  }, [basePath, t])

  const uploadAsset = useCallback(async (signedBlobId: string, name?: string): Promise<Asset | null> => {
    try {
      const response = await fetch(`${basePath}/assets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          signed_blob_id: signedBlobId,
          folder_id: currentFolder?.id,
          name
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to upload asset')
      }

      // Update local state
      setAssets(prev => [data.asset, ...prev])
      toast.success(t('toast.fileUploaded'))
      return data.asset
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.uploadFailed')
      toast.error(message)
      return null
    }
  }, [basePath, currentFolder, t])

  const renameAsset = useCallback(async (assetId: number, name: string): Promise<boolean> => {
    try {
      const response = await fetch(`${basePath}/assets/${assetId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ asset: { name } })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to rename asset')
      }

      setAssets(prev => prev.map(a => a.id === assetId ? data.asset : a))
      toast.success(t('toast.fileRenamed'))
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.renameFailed')
      toast.error(message)
      return false
    }
  }, [basePath, t])

  const moveAsset = useCallback(async (assetId: number, folderId: number | null): Promise<boolean> => {
    try {
      const response = await fetch(`${basePath}/assets/${assetId}/move`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ folder_id: folderId })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to move asset')
      }

      // Remove from current view if moved to different folder
      if (folderId !== currentFolder?.id) {
        setAssets(prev => prev.filter(a => a.id !== assetId))
      }
      toast.success(t('toast.fileMoved'))
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.moveFailed')
      toast.error(message)
      return false
    }
  }, [basePath, currentFolder, t])

  const copyAssetToProject = useCallback(async (
    assetId: number,
    projectId: number,
    folderId?: number | null
  ): Promise<Asset | null> => {
    try {
      const response = await fetch(`${basePath}/assets/${assetId}/copy_to_project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          project_id: projectId,
          folder_id: folderId
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to copy asset')
      }

      toast.success(t('toast.fileCopiedToProject'))
      return data.asset
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.copyFailed')
      toast.error(message)
      return null
    }
  }, [basePath, t])

  const copyAssetToOrganization = useCallback(async (
    assetId: number,
    folderId?: number | null
  ): Promise<Asset | null> => {
    try {
      const response = await fetch(`${basePath}/assets/${assetId}/copy_to_organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ folder_id: folderId })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to copy asset')
      }

      toast.success(t('toast.fileCopiedToOrganization'))
      return data.asset
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.copyFailed')
      toast.error(message)
      return null
    }
  }, [basePath, t])

  const deleteAsset = useCallback(async (assetId: number): Promise<boolean> => {
    try {
      const response = await fetch(`${basePath}/assets/${assetId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCsrfToken()
        }
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to delete asset')
      }

      setAssets(prev => prev.filter(a => a.id !== assetId))
      toast.success(t('toast.fileDeleted'))
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.deleteFailed')
      toast.error(message)
      return false
    }
  }, [basePath, t])

  const createFolder = useCallback(async (name: string, parentId?: number | null): Promise<AssetFolder | null> => {
    try {
      const response = await fetch(`${basePath}/asset_folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          name,
          parent_id: parentId ?? currentFolder?.id
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create folder')
      }

      // Update local state if creating in current folder
      if ((parentId ?? currentFolder?.id) === currentFolder?.id) {
        setFolders(prev => [...prev, data.folder].sort((a, b) => a.name.localeCompare(b.name)))
      }
      toast.success(t('toast.folderCreated'))
      return data.folder
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.createFolderFailed')
      toast.error(message)
      return null
    }
  }, [basePath, currentFolder, t])

  const renameFolder = useCallback(async (folderId: number, name: string): Promise<boolean> => {
    try {
      const response = await fetch(`${basePath}/asset_folders/${folderId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ folder: { name } })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to rename folder')
      }

      setFolders(prev => prev.map(f => f.id === folderId ? data.folder : f))
      toast.success(t('toast.folderRenamed'))
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.renameFolderFailed')
      toast.error(message)
      return false
    }
  }, [basePath, t])

  const deleteFolder = useCallback(async (folderId: number): Promise<boolean> => {
    try {
      const response = await fetch(`${basePath}/asset_folders/${folderId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCsrfToken()
        }
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to delete folder')
      }

      setFolders(prev => prev.filter(f => f.id !== folderId))
      toast.success(t('toast.folderDeleted'))
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.deleteFolderFailed')
      toast.error(message)
      return false
    }
  }, [basePath, t])

  return {
    folders,
    assets,
    currentFolder,
    breadcrumbs,
    isLoading,
    error,
    navigateToFolder,
    refresh,
    uploadAsset,
    renameAsset,
    moveAsset,
    copyAssetToProject,
    copyAssetToOrganization,
    deleteAsset,
    createFolder,
    renameFolder,
    deleteFolder,
    folderTree,
    loadFolderTree
  }
}
